"use client";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from "recharts";

interface BarEntry {
  label: string;
  value: number;
  color?: string;
}

interface BarChartBlockProps {
  data: BarEntry[];
  title?: string;
  caption?: string;
  /** Label shown in the tooltip after the value, e.g. "properties" */
  valueLabel?: string;
}

// Amber gradient matching the reference visualization palette
const DEFAULT_COLORS = [
  "#374151", // gray-700 - zero / low bucket
  "#D97706",
  "#F59E0B",
  "#FBBF24",
  "#FCD34D",
  "#F59E0B",
  "#D97706",
  "#B45309",
  "#92400E",
];

export default function BarChartBlock({
  data,
  title,
  caption,
  valueLabel = "count",
}: BarChartBlockProps) {
  if (!data?.length) return null;

  return (
    <figure className="my-8">
      {title && (
        <p className="font-sans font-medium text-black text-center text-base lg:text-lg mb-4">
          {title}
        </p>
      )}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 pt-5">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={data}
            margin={{ top: 4, right: 8, left: -16, bottom: 4 }}
          >
            <CartesianGrid
              vertical={false}
              stroke="#E5E7EB"
              strokeDasharray="0"
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#9CA3AF", fontWeight: 600 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9CA3AF" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                background: "#fff",
                border: "1px solid #E5E7EB",
                borderRadius: 8,
                fontSize: 13,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
              labelStyle={{ fontWeight: 600, color: "#111", marginBottom: 2 }}
              formatter={(value) => [`${value} ${valueLabel}`, ""]}
              cursor={{ fill: "rgba(0,0,0,0.04)" }}
            />
            <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={52}>
              {data.map((entry, index) => (
                <Cell
                  key={index}
                  fill={
                    entry.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {caption && (
        <figcaption className="text-center text-sm text-gray-500 mt-3">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
